import { useScrollReveal } from '../../hooks/useScrollReveal';

const PROCESS_STEPS = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    title: 'Site Consultation',
    description:
      'We evaluate your current lighting infrastructure and energy usage to identify the highest-impact savings opportunities and establish your project goals.',
  },
  {
    number: '02',
    icon: 'ri-draft-line',
    title: 'Photometric Design',
    description:
      'Our team engineers a signed and sealed photometric lighting plan, calibrated to precise foot-candle requirements and suitable for permit submission.',
  },
  {
    number: '03',
    icon: 'ri-lightbulb-flash-line',
    title: 'Product Specification',
    description:
      'We select premium commercial LED fixtures with modular designs optimized for heat dissipation, long-term output reliability, and your specific application type.',
  },
  {
    number: '04',
    icon: 'ri-tools-line',
    title: 'Installation & Rebates',
    description:
      'We coordinate the full installation process and manage utility rebate acquisition on your behalf — delivering your project from start to completion.',
  },
];

export default function ProcessSection() {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-charcoal py-28 lg:py-40 relative overflow-hidden">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 47px, rgba(201,168,76,0.6) 47px, rgba(201,168,76,0.6) 48px), repeating-linear-gradient(90deg, transparent, transparent 47px, rgba(201,168,76,0.6) 47px, rgba(201,168,76,0.6) 48px)',
        }}
      />
      {/* Left accent strip */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-gold/40 to-transparent" />

      <div className="relative z-10 mx-auto px-6 lg:px-12 xl:px-20">
        {/* Heading */}
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="block w-8 h-px bg-gold flex-shrink-0" />
              <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                Our Process
              </span>
            </div>
            <h2
              className="font-serif text-white leading-tight"
              style={{ fontSize: 'clamp(2rem, 3.8vw, 3.2rem)', fontWeight: 300 }}
            >
              From First Call to{' '}
              <em style={{ fontStyle: 'italic', color: '#c9a84c' }}>Final Fixture</em>
            </h2>
          </div>
          <p className="text-white/40 text-sm leading-[1.85] max-w-sm lg:text-right">
            A streamlined four-stage process designed to deliver precision results with minimal burden on your team.
          </p>
        </div>

        {/* Steps — horizontal timeline on desktop */}
        <div className="relative">
          {/* Connecting gold timeline line — desktop only */}
          <div
            className="hidden lg:block absolute top-[3.75rem] left-0 right-0 h-px bg-gradient-to-r from-gold/20 via-gold/50 to-gold/20"
            style={{ zIndex: 0 }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 lg:gap-0">
            {PROCESS_STEPS.map((step, i) => (
              <ProcessStep key={step.number} step={step} index={i} total={PROCESS_STEPS.length} />
            ))}
          </div>
        </div>

        {/* Bottom trust note */}
        <div
          className={`mt-16 pt-10 border-t border-white/8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 transition-all duration-700 delay-500 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-white/60 text-sm max-w-lg leading-relaxed">
            Every project is managed under one roof — no hand-offs, no coordination gaps, no multiple vendors.
          </p>
          <div className="flex items-center gap-6 flex-shrink-0">
            {['Design', 'Specification', 'Installation', 'Rebates'].map((tag, i) => (
              <div key={tag} className="flex items-center gap-2">
                {i > 0 && <span className="w-px h-3 bg-gold/30" />}
                <span className="text-white/60 text-[10px] tracking-[0.22em] uppercase font-medium">{tag}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProcessStep({
  step,
  index,
  total,
}: {
  step: (typeof PROCESS_STEPS)[0];
  index: number;
  total: number;
}) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  const isLast = index === total - 1;

  return (
    <div
      ref={ref}
      className={`relative flex flex-col transition-all duration-700 ease-out group ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      } ${!isLast ? 'border-b lg:border-b-0 lg:border-r border-white/8' : ''}`}
      style={{ transitionDelay: `${index * 130}ms` }}
    >
      {/* Top: timeline dot + number row */}
      <div className="flex items-center gap-4 px-8 lg:px-10 pt-0 pb-8 relative z-10">
        {/* Gold dot on timeline */}
        <div className="relative flex-shrink-0">
          {/* Outer ring */}
          <div className="w-7 h-7 rounded-full border border-gold/40 group-hover:border-gold flex items-center justify-center transition-all duration-300 bg-charcoal">
            <div className="w-2 h-2 rounded-full bg-gold group-hover:scale-125 transition-transform duration-300" />
          </div>
        </div>
        {/* Step number */}
        <span
          className="font-serif text-gold/30 group-hover:text-gold/60 transition-colors duration-300 leading-none"
          style={{ fontSize: 'clamp(2.5rem, 4vw, 4rem)', fontWeight: 300 }}
        >
          {step.number}
        </span>
      </div>

      {/* Card body */}
      <div className="px-8 lg:px-10 pb-10 lg:pb-14 flex-1">
        {/* Icon */}
        <div className="w-12 h-12 flex items-center justify-center border border-gold/25 group-hover:border-gold group-hover:bg-gold/8 transition-all duration-300 mb-7">
          <i className={`${step.icon} text-gold text-xl`}></i>
        </div>

        {/* Title */}
        <h3
          className="font-serif text-white leading-tight mb-4 group-hover:text-gold/90 transition-colors duration-300"
          style={{ fontSize: 'clamp(1.2rem, 1.8vw, 1.5rem)', fontWeight: 400 }}
        >
          {step.title}
        </h3>

        {/* Description */}
        <p className="text-white/40 text-sm leading-[1.9] group-hover:text-white/60 transition-colors duration-400">
          {step.description}
        </p>

        {/* Bottom gold accent line */}
        <div className="mt-8 w-8 h-px bg-gold/25 group-hover:w-14 group-hover:bg-gold/60 transition-all duration-500" />
      </div>
    </div>
  );
}
